/**
 * Rewards Configuration Page - Notion-Inspired Design
 * Configure loyalty program settings and bonus actions
 */

import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Checkbox, toast } from '../components/ui';
import { rewardsApi, RewardsConfig } from '../lib/api';

export const RewardsConfigPage: React.FC = () => {
  const [config, setConfig] = useState<RewardsConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editedConfig, setEditedConfig] = useState<Partial<RewardsConfig>>({});

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      const response = await rewardsApi.getConfig();
      const raw = response.data.data as any;
      // Normalize nested API response to the flat shape the component expects
      const normalized: Partial<RewardsConfig> = raw.baseRate
        ? {
            baseRateEnabled: raw.baseRate.enabled ?? true,
            b3trPerDollar: raw.baseRate.b3trPerDollar,
            minPurchase: raw.baseRate.minPurchase,
            redemptionEnabled: raw.redemption?.enabled ?? true,
            minRedemption: raw.redemption?.minRedemption,
            maxRedemption: raw.redemption?.maxRedemption,
            redemptionRate: raw.redemption?.redemptionRate,
            bonusActions: raw.bonusActions ?? [],
          }
        : raw;
      setConfig(normalized as RewardsConfig);
      setEditedConfig(normalized);
    } catch (error) {
      console.error('Failed to load rewards config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await rewardsApi.updateConfig(editedConfig);
      await loadConfig();
      toast.success('Rewards configuration saved!');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBonusActionToggle = async (actionId: string, enabled: boolean) => {
    try {
      await rewardsApi.updateBonusAction(actionId, { enabled });
      await loadConfig();
      toast.success(`Bonus action ${enabled ? 'enabled' : 'disabled'}.`);
    } catch (error) {
      console.error('Failed to toggle bonus action:', error);
      toast.error('Failed to update bonus action.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <h1 className="text-h1 font-bold text-black">Rewards Configuration</h1>
        <Card>
          <div className="h-64 bg-cream-100 rounded animate-pulse"></div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div>
        <h1 className="text-h1 font-bold text-black">Rewards Configuration</h1>
        <p className="text-base text-black-lighter mt-2">
          Configure how customers earn and redeem loyalty tokens
        </p>
      </div>

      {/* Base Earning Rate */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h3 font-semibold text-black">Base Earning Rate</h3>
          <p className="text-base mt-1 text-black-lighter">
            Configure how customers earn tokens from purchases
          </p>
        </div>

        <div className="p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between p-5 bg-cream-100 rounded-lg">
            <div>
              <p className="text-base font-semibold text-black">Enable base earning rate</p>
              <p className="text-base text-black-lighter mt-1">
                Automatically reward tokens based on purchase amount
              </p>
            </div>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={editedConfig.baseRateEnabled ?? true}
                onChange={(e) =>
                  setEditedConfig({ ...editedConfig, baseRateEnabled: e.target.checked })
                }
              />
              <span className="text-base">
                {editedConfig.baseRateEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>

          {editedConfig.baseRateEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="number"
                step="0.1"
                label="Stars per Dollar"
                placeholder="0.5"
                value={editedConfig.b3trPerDollar ?? ''}
                onChange={(e) =>
                  setEditedConfig({
                    ...editedConfig,
                    b3trPerDollar: parseFloat(e.target.value),
                  })
                }
                helperText="Number of Stars tokens earned per $1 spent"
              />

              <Input
                type="number"
                step="0.01"
                label="Minimum Purchase"
                placeholder="0.00"
                value={editedConfig.minPurchase ?? ''}
                onChange={(e) =>
                  setEditedConfig({
                    ...editedConfig,
                    minPurchase: parseFloat(e.target.value),
                  })
                }
                helperText="Minimum purchase amount to earn tokens"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Redemption Settings */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h3 font-semibold text-black">Redemption Settings</h3>
          <p className="text-base mt-1 text-black-lighter">
            Configure how customers redeem tokens for rewards
          </p>
        </div>

        <div className="p-6 flex flex-col gap-5">
          <div className="flex items-center justify-between p-5 bg-cream-100 rounded-lg">
            <div>
              <p className="text-base font-semibold text-black">Enable redemptions</p>
              <p className="text-base text-black-lighter mt-1">
                Allow customers to redeem tokens for discounts
              </p>
            </div>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={editedConfig.redemptionEnabled ?? true}
                onChange={(e) =>
                  setEditedConfig({
                    ...editedConfig,
                    redemptionEnabled: e.target.checked,
                  })
                }
              />
              <span className="text-base">
                {editedConfig.redemptionEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>

          {editedConfig.redemptionEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                type="number"
                step="1"
                label="Redemption Rate"
                placeholder="100"
                value={editedConfig.redemptionRate ?? ''}
                onChange={(e) =>
                  setEditedConfig({
                    ...editedConfig,
                    redemptionRate: parseFloat(e.target.value),
                  })
                }
                helperText="Stars tokens = $1"
              />

              <Input
                type="number"
                step="1"
                label="Minimum Redemption"
                placeholder="10"
                value={editedConfig.minRedemption ?? ''}
                onChange={(e) =>
                  setEditedConfig({
                    ...editedConfig,
                    minRedemption: parseFloat(e.target.value),
                  })
                }
                helperText="Minimum tokens to redeem"
              />

              <Input
                type="number"
                step="1"
                label="Maximum Redemption (Optional)"
                placeholder="1000"
                value={editedConfig.maxRedemption ?? ''}
                onChange={(e) =>
                  setEditedConfig({
                    ...editedConfig,
                    maxRedemption: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                helperText="Max tokens per transaction"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Bonus Actions */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-h3 font-semibold text-black">Bonus Actions</h3>
              <p className="text-base mt-1 text-black-lighter">
                Reward extra tokens for specific customer actions
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => toast.info('Bonus action management UI coming soon. Backend endpoints are ready.')}>
              Add Bonus Action
            </Button>
          </div>
        </div>

        <div className="p-6 flex flex-col gap-3">
          {config?.bonusActions?.map((action) => (
            <div
              key={action.id}
              className="flex items-center justify-between p-4 border border-border-light rounded-lg"
            >
              <div className="flex items-center gap-4">
                <div className="text-2xl">
                  {action.type === 'REUSABLE_CUP' && '♻️'}
                  {action.type === 'FIRST_VISIT' && '🎉'}
                  {action.type === 'REFERRAL' && '👋'}
                  {action.type === 'CUSTOM' && '⭐'}
                </div>
                <div>
                  <p className="text-base font-medium text-black">{action.name}</p>
                  <p className="text-base text-black-lighter">
                    +{action.bonusAmount} Stars tokens
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={action.enabled}
                  onChange={(e) =>
                    handleBonusActionToggle(action.id, e.target.checked)
                  }
                />
                <span className="text-base">
                  {action.enabled ? 'Active' : 'Inactive'}
                </span>
              </label>
            </div>
          ))}

          {(!config?.bonusActions || config.bonusActions.length === 0) && (
            <div className="text-center py-8">
              <p className="text-base text-black-lighter">No bonus actions configured yet</p>
              <p className="text-base text-black-lighter">Add bonus actions to reward special customer behaviors</p>
            </div>
          )}
        </div>
      </Card>

      {/* Preview */}
      <Card>
        <div className="p-6 border-b border-border-light">
          <h3 className="text-h3 font-semibold text-black">Earning Preview</h3>
          <p className="text-base mt-1 text-black-lighter">
            How customers will earn tokens with current settings
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-cream-100 rounded-lg">
            <p className="text-base text-black-lighter mb-1">$10 Purchase</p>
            <p className="text-h3 font-semibold text-black">
              {((editedConfig.b3trPerDollar || 0) * 10).toFixed(1)} Stars
            </p>
          </div>
          <div className="p-4 bg-cream-100 rounded-lg">
            <p className="text-base text-black-lighter mb-1">$50 Purchase</p>
            <p className="text-h3 font-semibold text-black">
              {((editedConfig.b3trPerDollar || 0) * 50).toFixed(1)} Stars
            </p>
          </div>
          <div className="p-4 bg-cream-100 rounded-lg">
            <p className="text-base text-black-lighter mb-1">$100 Purchase</p>
            <p className="text-h3 font-semibold text-black">
              {((editedConfig.b3trPerDollar || 0) * 100).toFixed(1)} Stars
            </p>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={loadConfig}>
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          isLoading={isSaving}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};
